import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Avatar,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Skeleton,
  Fab,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
  Radio,
  FormControlLabel,
  RadioGroup,
  Snackbar,
  Alert,
  useMediaQuery,
  keyframes,
} from "@mui/material";
import { styled } from "@mui/system";
import {
  Share2,
  BookOpen,
  Copy,
  CheckCircle,
  Square,
  Trash2,
  ThumbsDown,
  Eye,
  ChevronDown,
  GitBranch,
  FileText,
  Mic,
  Bookmark,
  Award,
  Info,
  Send,
  Zap,
  Menu,
  Download,
} from "react-feather";
import { v4 as uuidv4 } from "uuid";
import { saveAs } from "file-saver";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getChatTitles, createChatSession } from "../actions/chatActions";
import AlertDialog from "../dialogs/AlertDialog";
import Feedback from "../dialogs/ChatFeedbackDialog";
import { trackEvent, trackEventWithResponseTime } from "../analytics";
import { formatRefLabel, wait } from "../utils/HelperFunction/textFormatting";
import { formatTimestamp } from "../utils/HelperFunction/utils";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CaseLawPreviewDialog from "../dialogs/CaseLawPreviewDialog";
import { findCaseFromAzureAiSearch } from "../actions/searchActions";
import Markdown from "react-markdown";
import InfoDialog from "../dialogs/InfoDialog";
import lawgptLogo from "./../assets/images/lawgptLogo.png";
import modelLogo from "./../assets/images/modelLogo.png";
import { showInsufficientCreditsModal } from "../actions/creditActions";
import { deductCredits } from "../utils/HelperFunction/deductCredits";
import { fetchBookmarkGroups } from "../store/Bookmarks/bookmarksActions";
import AudioVisualizer from "./UI/AudioVisualizer";
import useAudioProcessor from "../utils/hooks/useAudioProcessor";
import BookmarkDialog from "../dialogs/BookmarkDialog";
import StatuteContentDialog from "../dialogs/StatuteContentDialog";
import LoadingAnimationWithText from "./UI/LoadingAnimationWithText";
import {
  fetchChatTitleApi,
  sendStreamingChatMessageApi,
  rephraseQueryApi,
  saveChatHistoryApi,
} from "../services/api/legalChatApi";
import { removeItemFromGroupApi } from "../services/api/bookmarksApi";
import { searchStatutesApi } from "../services/api/statuteSearchApi";
import {
  parseSSEStream,
  buildConversationHistory,
} from "../utils/HelperFunction/utils";
import { InfoIcon } from "lucide-react";
import LogoLoader from "./UI/LogoLoader";

export const markdownStyles = {
  ul: {
    margin: "0px",
    paddingLeft: "20px",
  },
  ol: {
    margin: "0px",
    paddingLeft: "20px",
    marginTop: "2px",
  },
  li: {
    margin: "0px",
    padding: "0px",
    marginTop: "2px",
  },
  p: {
    margin: "0px",
    padding: "0px",
  },
  pre: {
    whiteSpace: "break-spaces",
  },
};

const pulse = keyframes` 0% {
    box-shadow: 0 0 0 0 rgba(0, 115, 166, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 115, 166, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 115, 166, 0);
  }`;

export const StyledImage = styled("img")(({ theme }) => ({
  maxWidth: "180px",
  height: "auto",
  objectFit: "contain",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "120px",
  },
}));

// Delay between each displayed word/chunk during streaming (ms). Lower = faster, higher = slower.
// ┌───────┬─────────────────────────────────────────┐
// │ Value │                 Effect                  │
// ├───────┼─────────────────────────────────────────┤
// │ 10    │ Very fast (~100 words/sec)              │
// ├───────┼─────────────────────────────────────────┤
// │ 25    │ Current default (~40 words/sec)         │
// ├───────┼─────────────────────────────────────────┤
// │ 50    │ Slower, more deliberate (~20 words/sec) │
// ├───────┼─────────────────────────────────────────┤
// │ 80    │ Slow typewriter feel (~12 words/sec)    │
// └───────┴─────────────────────────────────────────┘
const STREAM_WORD_DELAY_MS = 10;

// Default Function Component
const ChatInterface = ({ toggleSidebar }) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [isLoading, setIsLoading] = useState(false);
  const [chatTitle, setChatTitle] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [finalChatId, setFinalChatId] = useState("");
  const [alignment, setAlignment] = useState("balanced");
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const [response, setResponse] = useState("");
  const [feedbackQuery, setFeebackQuery] = useState("");
  const [feedbackRefs, setFeedbackRefs] = useState([]);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [messageIndex, setMessageIndex] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [openBookmarkDialog, setOpenBookmarkDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [bookmarkedCaseIds, setBookmarkedCaseIds] = useState(new Set());
  const [selectedStatute, setSelectedStatute] = useState(null);
  const [bookmarkedStatuteIds, setBookmarkedStatuteIds] = useState(new Set());
  const [openStatuteModal, setOpenStatuteModal] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [isRewriting, setIsRewriting] = useState(false);
  const [activeTools, setActiveTools] = useState([]);
  const [streamingText, setStreamingText] = useState("");
  const [streamingRefs, setStreamingRefs] = useState([]);
  const audioData = useAudioProcessor(isListening);

  const chatContainerStartRef = useRef(null);
  const chatContainerEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const chatHistory = useSelector((state) => state.chatHistory);
  const { loading: historyLoading, history, error: historyError } = chatHistory;
  const showEmptyHistoryNotice =
    (!!history &&
      Array.isArray(history.messages) &&
      history.messages.length === 0) ||
    historyError === "No chat history found.";

  const newChat = useSelector((state) => state.newChat);
  const { isClicked } = newChat;

  const chatTitles = useSelector((state) => state.chatTitles);
  const { successEdit, titles: chatTitleList = [] } = chatTitles;
  // Check if sufficient credits are available
  const credits = useSelector((state) => state.credit.credits); // Get credits from Redux store
  const userId = useSelector((state) => state.credit.userId); // Get userId from Redux store
  const bookmarkGroups = useSelector((state) => state.bookmarkGroups);
  const { groups } = bookmarkGroups;

  const handleCloseBookmarkDialog = () => {
    setOpenBookmarkDialog(false);
    setSelectedGroup("");
  };

  const handleCloseStatuteBookmarkDialog = () => {
    setOpenStatuteModal(false);
    setSelectedGroup("");
  };

  const handleClickInfoButton = () => {
    setOpenInfoDialog(true);
  };

  const handleCloseInfoDialog = () => {
    setOpenInfoDialog(false);
  };

  const handlePreviewClick = (caseItem) => {
    dispatch(findCaseFromAzureAiSearch(caseItem.id));
    setOpenPreview(true);
  };

  const handleCloseAlertDialog = () => {
    setOpenAlertDialog(false);
  };

  const handleInputChange = (event) => {
    setInputMessage(event.target.value);
  };

  const handleFeedbackGiven = (messageIndex) => {
    setFeedbackGiven({ ...feedbackGiven, [messageIndex]: true });
  };

  const formatCaseTitle = (title) => {
    if (!title) return;
    const splitTitle = title.split("Honorable")[0];
    const titleCaseTitle = splitTitle
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return titleCaseTitle;
  };

  const formatCourtName = (courtName) => {
    if (!courtName) return "";

    return courtName
      .toLowerCase()
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const generateChatbotResponse = useCallback(
    async (userMessage, conversationHistory) => {
      let eventAction = "Free Form Query";
      if (alignment === "balanced") eventAction = "Free Form Query";
      else if (alignment === "precise") eventAction = "Structured Query";
      else if (alignment === "case law") eventAction = "Case Law Query";

      const startTime = new Date();
      trackEvent("Chat", "UserQuery", `User: ${userInfo.name}`);

      let finalText = "";
      let finalRefs = [];
      let finalMetadata = {};
      let finalTitle = "";

      abortControllerRef.current = new AbortController();

      try {
        setIsLoading(true);
        setActiveTools([]);
        setStreamingText("");
        setStreamingRefs([]);

        const readableStream = await sendStreamingChatMessageApi(
          conversationHistory,
          abortControllerRef.current.signal,
        );

        await parseSSEStream(readableStream, async ({ type, data }) => {
          switch (type) {
            case "title":
              finalTitle = data.title;
              break;
            case "tool_start":
              setActiveTools((prev) => [
                ...prev,
                {
                  tool_name: data.tool_name,
                  label: data.label,
                  status: "running",
                },
              ]);
              break;
            case "tool_end":
              setActiveTools((prev) =>
                prev.map((t) =>
                  t.tool_name === data.tool_name ? { ...t, status: "done" } : t,
                ),
              );
              break;
            case "token": {
              finalText += data.text;
              // Split token into word-sized chunks and display each with a short delay
              const chunks = data.text.match(/\S+|\s+/g) || [data.text];
              for (const chunk of chunks) {
                setStreamingText((prev) => prev + chunk);
                // await new Promise((resolve) => setTimeout(resolve, STREAM_WORD_DELAY_MS));
              }
              break;
            }
            case "references":
              finalRefs = data.references;
              setStreamingRefs(data.references);
              break;
            case "done":
              finalMetadata = data.metadata || {};
              break;
            default:
              break;
          }
        });

        const responseTimeMilliseconds = new Date() - startTime;
        trackEvent("Chat", eventAction, `User: ${userInfo.name}`);
        trackEventWithResponseTime(
          "Chat",
          "Response Time",
          `User Message: ${userMessage}`,
          responseTimeMilliseconds,
        );

        return {
          response: finalText,
          references: finalRefs,
          metadata: finalMetadata,
          title: finalTitle,
        };
      } catch (error) {
        if (error.name !== "AbortError") {
          toast.error("Something went wrong. Please try again later.", {
            position: "top-right",
          });
        }
        return finalText
          ? {
              response: finalText,
              references: finalRefs,
              metadata: finalMetadata,
              title: finalTitle,
            }
          : null;
      } finally {
        setIsLoading(false);
        setActiveTools([]);
      }
    },
    [alignment, userInfo],
  );

  const handleSendMessage = useCallback(async () => {
    // Ignore empty messages
    if (inputMessage.trim() === "") return;
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    if (location.pathname === "/app/home") {
      return navigate(`/app/legal-chat?query=${inputMessage}`);
    }
    trackEvent("Chat", "Chat Initiated", `User: ${userInfo.name}`);
    const newMessages = [
      ...(messages || []),
      { text: { response: inputMessage }, isUser: true },
    ];
    setMessages(newMessages);
    setInputMessage("");

    const conversationHistory = buildConversationHistory(
      messages,
      inputMessage,
    );
    const userMessageTimestamp = Date.now();
    const chatbotResponse = await generateChatbotResponse(
      inputMessage,
      conversationHistory,
    );

    if (chatbotResponse) {
      const updatedMessages = [
        ...newMessages,
        {
          text: chatbotResponse,
          isUser: false,
        },
      ];
      setMessages(updatedMessages);
      setStreamingText("");
      setStreamingRefs([]);

      // Deduct credits using the helper
      const deductionSuccess = deductCredits(
        { userId, creditsToDeduct: 1, event: "Send Message on Legal Chat" },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }

      await saveMessages(
        inputMessage,
        chatbotResponse,
        finalChatId,
        userInfo._id,
        userMessageTimestamp,
      );
    } else {
      setStreamingText("");
      setStreamingRefs([]);
    }

    if (messages.length === 0) {
      if (chatbotResponse?.title) {
        setChatTitle(chatbotResponse.title);
      }
      const createdSession = await dispatch(
        createChatSession(
          userInfo._id,
          finalChatId,
          alignment,
          inputMessage,
          chatbotResponse?.title,
        ),
      );
      dispatch(getChatTitles(userInfo?._id));
      if (createdSession?.chat_title) {
        setChatTitle(createdSession.chat_title);
      }
    }
  }, [
    dispatch,
    location,
    navigate,
    inputMessage,
    generateChatbotResponse,
    userInfo,
    messages,
  ]);

  const saveMessages = async (
    userMessage,
    aiResponse,
    chatId,
    userId,
    userTimestamp,
  ) => {
    const messagesPayload = [
      {
        content: userMessage,
        role: "user",
        timestamp: formatTimestamp(userTimestamp || Date.now()),
      },
      {
        role: "assistant",
        content: aiResponse?.response || "",
        response: aiResponse?.response || "",
        references: aiResponse?.references || [],
        metadata: aiResponse?.metadata || {},
        timestamp: formatTimestamp(Date.now()),
      },
    ];

    try {
      await saveChatHistoryApi(userId, chatId, messagesPayload);
    } catch (error) {
      console.error("Failed to save chat history:", error);
    }
  };

  const handleCopyClick = async (responseText) => {
    setIsCopied(true);
    try {
      await navigator.clipboard.writeText(responseText);
      await wait();
      setIsCopied(false);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleExportChat = () => {
    const chatText = messages
      .map((message) => {
        return message.isUser
          ? `User: ${message.text.response}`
          : `Assistant: ${message.text.response}\nReferences:\n${
              message.text.references
                ? message.text.references
                    ?.map((ref) => {
                      return `Label: ${ref.label}\nContent: ${ref.content}\n URL: ${ref.url}`;
                    })
                    .join("\n\n")
                : "Not Applicable"
            }`;
      })
      .join("\n");

    const blob = new Blob([chatText], { type: "text/plain" });

    saveAs(blob, `${chatTitle}.txt`);
  };

  const handleStatutePreview = async (ref) => {
    setModalContent({
      content: ref.html_content || "<p>Loading…</p>",
      label: ref.label,
    });
    setOpenModal(true);

    if (ref.html_content || !ref.id) return;

    try {
      const response = await searchStatutesApi({
        query: "",
        sort_by: "",
        filter: `Bookmark_ID eq ${ref.id}`,
        search_field_mode: "Title",
        top: 1,
        skip: 0,
        count: true,
      });
      const match = response.data?.search_results?.[0];
      setModalContent({
        label: match?.Statute_Name || ref.label,
        content:
          match?.related_data?.[0]?.Statute_HTML ||
          "<p>No content available.</p>",
      });
    } catch (error) {
      setModalContent({
        label: ref.label,
        content: "<p>Failed to load statute content.</p>",
      });
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleChangeChat = (event) => {
    setAlignment(event.target.value);
  };

  const handleCloseFeedbackDialog = () => {
    setOpenFeedbackDialog(false);
  };

  const handleFeedbackClick = (response, references, index) => {
    const userQuery = messages[index - 1]?.text.response;
    setFeebackQuery(userQuery);
    setFeedbackRefs(references);
    setResponse(response);
    setOpenFeedbackDialog(true);
    setMessageIndex(index);
  };

  const handleScroll = () => {
    const container = chatContainerStartRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const threshold = 100;
      const shouldShow = scrollTop + clientHeight < scrollHeight - threshold;
      setShowScrollButton(shouldShow);
    }
  };

  const scrollToBottom = () => {
    chatContainerEndRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const startRecognition = () => {
    setIsListening(true);
    shouldListenRef.current = true;
    recognitionRef.current?.start();
  };

  const stopRecognition = () => {
    recognitionRef.current?.stop();
    shouldListenRef.current = false;
    setIsListening(false);
  };

  const cancelVoiceInput = () => {
    stopRecognition();
    setInputMessage("");
  };

  const acceptVoiceInput = () => {
    stopRecognition();
    if (!inputMessage.trim()) return;
    //handleSendMessage();
  };

  const handleBookmarkClick = (caseItem) => {
    setSelectedCase(caseItem);
    const defaultGroup = groups.find(
      (group) => group.groupName === "Bookmarks",
    );
    setSelectedGroup(
      defaultGroup === undefined ? groups[0]._id : defaultGroup._id,
    );
    setOpenBookmarkDialog(true);
  };

  const handleStatuteBookmark = (statute) => {
    setSelectedStatute(statute);
    const defaultGroup = groups.find(
      (group) => group.groupName === "Bookmarks",
    );

    setSelectedGroup(
      defaultGroup === undefined ? groups[0]._id : defaultGroup._id,
    );
    setOpenStatuteModal(true);
  };

  const findGroupIdByCaseId = (documents, caseId) => {
    // Find the document where the caseId exists in the 'cases' array
    const group = documents.find((doc) =>
      doc.cases.some((caseItem) => caseItem.id === caseId),
    );

    // Return the _id of the group or null if not found
    return group ? group._id : null;
  };

  const handleUnBookmark = async (caseId) => {
    try {
      const groupId = findGroupIdByCaseId(groups, caseId);
      const response = await removeItemFromGroupApi(
        userInfo._id,
        groupId,
        caseId,
        null,
      );

      if (response.status === 200) {
        setSnackbarMessage({
          type: "success",
          text: "Bookmark Removed!",
        });
        setBookmarkedCaseIds((prevIds) => {
          const updatedIds = new Set(prevIds);
          updatedIds.delete(caseId);
          return updatedIds;
        });
      } else {
        console.error("Error deleting bookmarked case");
      }
    } catch (error) {
      console.error("Error deleting bookmarked case:", error);
    }
  };

  const findGroupIdByStatuteId = (documents, statuteId) => {
    const group = documents.find((doc) =>
      doc.statutes.some((statuteItem) => statuteItem.id === statuteId),
    );

    return group ? group._id : null;
  };

  const handleUnBookmarkStatute = async (statuteId) => {
    try {
      const groupId = findGroupIdByStatuteId(groups, statuteId);
      const response = await removeItemFromGroupApi(
        userInfo._id,
        groupId,
        null,
        statuteId,
      );

      if (response.status === 200) {
        setBookmarkedStatuteIds((prevIds) => {
          const updatedIds = new Set(prevIds);
          updatedIds.delete(statuteId);
          return updatedIds;
        });
      } else {
        console.error("Error deleting bookmarked statute");
      }
    } catch (error) {
      console.error("Error deleting bookmarked statute:", error);
    }
  };

  useEffect(() => {
    // Fetch bookmark groups when the component mounts or when userInfo changes
    if (userInfo._id) {
      dispatch(fetchBookmarkGroups(userInfo._id));
    }
  }, []);

  useEffect(() => {
    if (groups) {
      // Extract all case IDs from the cases array in each group document
      const allCaseIds = groups?.flatMap((group) =>
        group.cases.map((caseItem) => caseItem.id),
      );

      setBookmarkedCaseIds(new Set(allCaseIds)); // Store IDs in a Set for fast lookup
      const defaultGroup = groups?.find(
        (group) => group.groupName === "Bookmarks",
      );
      setSelectedGroup(defaultGroup?._id);

      // Extract all statute IDs from the statutes array in each group document
      const allStatuteIds = groups?.flatMap((group) =>
        group.statutes.map((statute) => statute.id),
      );

      // Set the bookmark groups and statute IDs
      setBookmarkedStatuteIds(new Set(allStatuteIds)); // Store IDs in a Set for fast lookup
    }
  }, [groups]);

  useEffect(() => {
    if (location.pathname === "/app/home") {
      setShowScrollButton(false);
      setMessages([]);
      setChatTitle("");
      dispatch({ type: "CHAT_HISTORY_RESET" });
    }
  }, [location, dispatch]);

  useEffect(() => {
    if (historyLoading) {
      setFeedbackGiven({});
    }
  }, [historyLoading]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const receivedQuery = queryParams.get("query");
    if (receivedQuery) {
      setInputMessage(receivedQuery);

      const handleMessageAndUpdateUrl = async () => {
        // Introduce a slight delay before clearing the URL
        setTimeout(() => {
          queryParams.delete("query");
          navigate({ search: queryParams.toString() }, { replace: true });
        }, 100);
        await handleSendMessage();
      };
      handleMessageAndUpdateUrl();
    }
  }, [location, handleSendMessage, navigate]);

  useEffect(() => {
    const container = chatContainerStartRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [history, messages]);

  useEffect(() => {
    if (successEdit) {
      const editedChat = chatTitleList.find(
        (chat) => chat.chat_id === finalChatId,
      );
      if (editedChat?.chat_title) {
        setChatTitle(editedChat.chat_title);
      }
      dispatch({ type: "SUCCESS_EDIT_RESET" });
    }
  }, [successEdit, dispatch, chatTitleList, finalChatId]);

  useEffect(() => {
    if (chatContainerEndRef.current) {
      chatContainerEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Auto-scroll to bottom on every word update during streaming, but only
  // while the user is already at the bottom — don't fight a manual scroll-up
  useEffect(() => {
    if (!streamingText || !chatContainerStartRef.current || showScrollButton) return;
    chatContainerStartRef.current.scrollTop =
      chatContainerStartRef.current.scrollHeight;
  }, [streamingText, showScrollButton]);

  useEffect(() => {
    if (isClicked || !history) {
      setShowScrollButton(false);
      setMessages([]);
      setChatTitle("");
      cancelVoiceInput();
      dispatch({ type: "DELETE_CHAT_RESET" });
      dispatch({ type: "NEW_CHAT_RESET" });
    }
  }, [dispatch, isClicked, history]);

  useEffect(() => {
    //Add history to chat interface
    if (history) {
      const historyMessages = history.messages;
      dispatch({ type: "NEW_CHAT_RESET" });
      setFinalChatId(history.chat_id);
      const selectedChat = chatTitleList.find(
        (chat) => chat?.chat_id === history.chat_id,
      );
      setChatTitle(selectedChat?.chat_title || history.chat_title || "");
      setAlignment("balanced");
      cancelVoiceInput();
      if (historyMessages?.length > 0) {
        const firstContent = historyMessages[0].content;
        if (
          firstContent.startsWith("Retrieve") ||
          firstContent.startsWith("retrieve")
        ) {
          setAlignment("case law");
        }
      }
      if (historyMessages?.length > 0) {
        const firstContent = historyMessages[0].content;
        if (firstContent.startsWith("search")) {
          setAlignment("precise");
        }
      }
      const mappedMessages =
        historyMessages?.length > 0 &&
        historyMessages.map((message) => {
          return {
            text: {
              response: message.response || message.content,
              references: message.references,
            },
            isUser: message.role === "user",
          };
        });
      setMessages(mappedMessages || []);
    }
  }, [history, dispatch, chatTitleList]);

  useEffect(() => {
    return () => {
      dispatch({ type: "CHAT_HISTORY_RESET" });
    };
  }, [dispatch]);

  useEffect(() => {
    const newChatId = uuidv4();

    if (!history) {
      setFinalChatId(newChatId);
      setAlignment("balanced");
    }
  }, [history, dispatch, isClicked]);

  useEffect(() => {
    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      if (!shouldListenRef.current) return;

      const interimTranscript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join("");
      setInputMessage(interimTranscript);
    };

    recognition.onerror = (event) => {
      console.error("Speech error:", event.error);
      stopRecognition();
    };

    recognition.onend = () => {
      if (shouldListenRef.current) {
        recognition.start();
      } else {
        // actually stopped by user
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;
  }, []);

  // Add this function to your component
  const handleRewrite = async () => {
    if (!inputMessage.trim() || isRewriting) return;
    try {
      setIsRewriting(true);
      // Check if there's text to rewrite
      if (!inputMessage.trim()) return;

      // Call the rewrite API
      const data = await rephraseQueryApi(inputMessage);

      // Update the input field with the rewritten query
      setInputMessage(data.answer); // Use data.answer from the response
    } catch (error) {
      console.error("Rewrite error:", error);
    } finally {
      setIsRewriting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>LawGPT - Legal Chat</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          boxSizing: "border-box",
          flexDirection: "column",
          height: "calc(100vh - 64px)",
          width: "100%",
          maxWidth: { xs: "unset", lg: "83.2%" },
          p: "16px",
          backgroundColor: "#F8FAFC",

          "@media (min-width: 1920px)": {
            width: "83.3%",
            maxWidth: "83.3%",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            boxSizing: "border-box",
            flexDirection: "column",
            bgcolor: "#FFFFFF",
            borderRadius: "16px",
            flexGrow: 1,
            border: "1px solid #F1F5F9",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Mobile Menu Button - Only visible on mobile */}
          <Box
            sx={{
              display: { xs: "flex", lg: "none" },
              position: "absolute",
              top: "16px",
              left: "16px",
              zIndex: 10,
            }}
          >
            <IconButton
              size="small"
              sx={{
                color: "#6B7280",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
              }}
              onClick={toggleSidebar}
            >
              <Menu size={20} />
            </IconButton>
          </Box>

          {/* Export Button - Only visible when there's chat history */}
          {(history || chatTitle) && (
            <Box
              sx={{
                position: "absolute",
                top: "16px",
                right: "16px",
                zIndex: 10,
              }}
            >
              <Tooltip
                title="Export Chat"
                arrow
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "#111827",
                      color: "#FFFFFF",
                      fontSize: "12px",
                    },
                    "& .MuiTooltip-arrow": {
                      color: "#111827",
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleExportChat}
                  sx={{
                    color: "#0073A6",
                    "&:hover": {
                      backgroundColor: "#F0F9FF",
                    },
                  }}
                >
                  <Download size={20} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {/* Chat start view */}
          {messages.length === 0 && !historyLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: { xs: "100%", sm: "80%", md: "70%" },
                width: { xs: "90%", sm: "80%", md: "60%" },
                marginLeft: "auto",
                marginRight: "auto",
                gap: { xs: "1rem", sm: "1.5rem" },
              }}
            >
              {showEmptyHistoryNotice && (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "520px",
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    borderRadius: "16px",
                    border: "1px solid #cfe3ff",
                    background:
                      "linear-gradient(180deg, rgba(240,248,255,0.95) 0%, rgba(227,242,253,0.95) 100%)",
                    boxShadow: "0 10px 30px rgba(48, 63, 159, 0.08)",
                    textAlign: "center",
                  }}
                >
                  <InfoIcon sx={{ color: "#1976d2", fontSize: 34, mb: 0.5 }} />
                  <Typography
                    variant="h6"
                    sx={{ color: "#103b73", fontWeight: 700, mb: 0.5 }}
                  >
                    No chat history found
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#4c5b6b", lineHeight: 1.6 }}
                  >
                    This session does not have any saved messages yet. Start a
                    new conversation to see responses and references here.
                  </Typography>
                </Box>
              )}

              {!showEmptyHistoryNotice && (
                <>
                  <Box
                    sx={{
                      width: "100%",
                      mb: { xs: 2, md: 4 },
                      textAlign: "center",
                      alignItems: "center",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { xs: "28px", md: "36px" },
                        fontWeight: 700,
                        color: "#111827",
                        fontFamily: "'Inter', 'Inter Fallback', sans-serif",
                        mb: 1,
                      }}
                    >
                      Legal Chat
                    </Typography>
                    {/* Engaging Welcome Message */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "16px", md: "18px" },
                        color: "#111827",
                        fontFamily: "'Inter', 'Inter Fallback', sans-serif",
                        fontWeight: 500,
                        textAlign: "center",
                        mb: 1,
                      }}
                    >
                      How can I help you with your legal research today?
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "13px", md: "14px" },
                        color: "#6B7280",
                        fontFamily: "'Inter', 'Inter Fallback', sans-serif",
                        fontWeight: 400,
                        textAlign: "center",
                        maxWidth: "500px",
                        lineHeight: 1.5,
                        mb: 2,
                      }}
                    >
                      I can help you analyze cases, research statutes, draft
                      legal documents, or answer any legal questions you might
                      have.
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Box
              ref={chatContainerStartRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                width: "100%",
                overflowY: "auto",
                padding: { xs: "16px", md: "24px" },
                gap: 2,
                "::-webkit-scrollbar": {
                  width: "6px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#F8FAFC",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "8px",
                },
                "::-webkit-scrollbar-thumb:hover": {
                  background: "#94A3B8",
                },
              }}
            >
              {/* Chat Title - Only show when there are messages or a title */}
              {(chatTitle || messages.length > 0) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                    mb: 3,
                    pt: 2,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: { xs: "24px", md: "26px" },
                      fontWeight: 600,
                      color: "#111827",
                      textAlign: "left",
                      maxWidth: "600px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      px: 2,
                    }}
                  >
                    {chatTitle || "New Chat"}
                  </Typography>
                </Box>
              )}

              {/* Centered Messages Container - ChatGPT Style */}
              <Box
                sx={{
                  maxWidth: { xs: "100%", md: "768px", lg: "900px" },
                  width: "100%",
                  margin: "0 auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <style>
                  {`
          div::-webkit-scrollbar {
            width: 5px;
          }
          div::-webkit-scrollbar-track {
            background: #fff;
          }
          div::-webkit-scrollbar-thumb {
            background: #bbb;
            border-radius: 6px;
          }
        `}
                </style>
                {historyLoading ? (
                  <Box
                    sx={{
                      height: "100%",
                      margin: "auto",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s"  />
                  </Box>
                ) : (
                  messages.length > 0 &&
                  messages.map((message, messageIndex) => (
                    <Box
                      key={`${uuidv4()}-${messageIndex}`}
                      sx={{
                        display: "flex",
                        justifyContent: message.isUser
                          ? "flex-end"
                          : "flex-start",
                        width: "100%",
                        mb: 3,
                      }}
                    >
                      {!message.isUser && (
                        <Avatar
                          variant="circle"
                          sx={{
                            display: { xs: "none", md: "flex" },
                            width: "36px",
                            height: "36px",
                            marginRight: "12px",
                            marginTop: "4px",
                            padding: "4px",
                            background: "white",
                            border: "1px solid #E2E8F0",
                            "& img": {
                              objectFit: "contain",
                              width: "100%",
                              height: "100%",
                            },
                          }}
                          src={modelLogo}
                        />
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          backgroundColor: message.isUser
                            ? "#0073A6"
                            : "#FFFFFF",
                          color: message.isUser ? "#FFFFFF" : "#111827",
                          alignSelf: message.isUser ? "flex-end" : "flex-start",
                          borderRadius: "16px",
                          border: message.isUser ? "none" : "1px solid #E2E8F0",
                          boxShadow: message.isUser
                            ? "0 2px 8px rgba(0, 115, 166, 0.2)"
                            : "0 1px 3px rgba(0, 0, 0, 0.08)",
                          maxWidth: message.isUser
                            ? { xs: "80%", md: "65%" }
                            : { xs: "90%", md: "80%" },
                          minWidth: "200px",
                        }}
                      >
                        <Typography
                          variant="body1"
                          component="div"
                          sx={{
                            width: "100%",
                            padding: { xs: "16px", md: "20px" },
                            color: "inherit",
                            boxSizing: "border-box",
                            fontSize: { xs: "14px", md: "15px" },
                            lineHeight: 1.6,
                            fontFamily: "'Inter', 'Inter Fallback', sans-serif",
                          }}
                        >
                          <Markdown
                            components={{
                              ul: ({ ...props }) => (
                                <ul
                                  style={{
                                    ...markdownStyles.ul,
                                    fontSize: isMobileView ? "13px" : "14px",
                                  }}
                                  {...props}
                                />
                              ),
                              ol: ({ ...props }) => (
                                <ol
                                  style={{
                                    ...markdownStyles.ol,
                                    fontSize: isMobileView ? "13px" : "14px",
                                  }}
                                  {...props}
                                />
                              ),
                              li: ({ ...props }) => (
                                <li
                                  style={{
                                    ...markdownStyles.li,
                                    fontSize: isMobileView ? "13px" : "14px",
                                  }}
                                  {...props}
                                />
                              ),
                              p: ({ ...props }) => (
                                <Typography
                                  sx={{
                                    ...markdownStyles.p,
                                    fontSize: { xs: "13px", lg: "14px" },
                                  }}
                                  {...props}
                                  component="p"
                                />
                              ),
                              pre: ({ ...props }) => (
                                <Typography
                                  style={markdownStyles.pre}
                                  {...props}
                                  component="pre"
                                />
                              ),
                              strong: ({ ...props }) => (
                                <Typography
                                  sx={{
                                    fontSize: { xs: "14px", lg: "15px" },
                                    fontWeight: "bold",
                                  }}
                                  {...props}
                                  component="strong"
                                />
                              ),
                            }}
                          >
                            {message.text.response}
                          </Markdown>
                          {message.text.references?.filter(
                            (reference) => reference.type === "statute",
                          ).length > 0 && (
                            <Typography
                              component="div"
                              sx={{
                                fontWeight: "bold",
                                marginTop: 2,
                                marginBottom: "8px",
                                fontSize: { xs: "13px", lg: "14px" },
                              }}
                            >
                              Relevant Statutes:
                            </Typography>
                          )}
                          {message.text?.references
                            ?.filter(
                              (reference) => reference.type === "statute",
                            )
                            .map((ref, refIndex) => (
                              <Box
                                key={`${uuidv4()}-${refIndex}`}
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  mb: "5px",
                                  alignItems: {
                                    xs: "flex-start",
                                    lg: "center",
                                  },
                                  gap: "4px",
                                }}
                              >
                                <Stack
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                  }}
                                >
                                  <IconButton
                                    onClick={() => handleStatutePreview(ref)}
                                    size="small"
                                  >
                                    <Tooltip title="View">
                                      <Eye
                                        size={16}
                                        color="#2196f3"
                                        style={{ marginRight: "8px" }}
                                      />
                                    </Tooltip>
                                  </IconButton>
                                  <IconButton
                                    sx={{
                                      p: 0,
                                      color: "#d2042d",
                                      "&:hover": {
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                  >
                                    {bookmarkedStatuteIds.has(ref.id) ? (
                                      <>
                                        <Tooltip title="Remove bookmark">
                                          <Award
                                            onClick={() =>
                                              handleUnBookmarkStatute(ref.id)
                                            }
                                            size={16}
                                            color="#d2042d"
                                          />
                                        </Tooltip>
                                      </>
                                    ) : (
                                      <Tooltip title="Bookmark">
                                        <Bookmark
                                          onClick={() =>
                                            handleStatuteBookmark(ref)
                                          }
                                          sx={{
                                            "&:hover": {
                                              backgroundColor: "transparent",
                                            },
                                          }}
                                          size={16}
                                        />
                                      </Tooltip>
                                    )}
                                  </IconButton>
                                </Stack>
                                <Typography
                                  component="div"
                                  sx={{
                                    fontSize: { xs: "13px", lg: "14px" },
                                    display: "flex",
                                    alignItems: {
                                      xs: "flex-start",
                                      lg: "center",
                                    },
                                  }}
                                >
                                  {formatRefLabel(ref.label)}
                                </Typography>
                              </Box>
                            ))}
                          {/* UI for the Case Law References */}
                          {message.text.references?.filter(
                            (reference) =>
                              reference.type !== "case" &&
                              reference.type !== "statute",
                          ).length > 0 && (
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                marginBottom: "6px",
                                marginTop: 2,
                                fontSize: { xs: "13px", lg: "14px" },
                              }}
                            >
                              Relevant Case Laws:
                            </Typography>
                          )}
                          {message.text.references
                            ?.filter(
                              (reference) =>
                                reference.type !== "case" &&
                                reference.type !== "statute",
                            )
                            .map((law, index) => (
                              <React.Fragment key={index}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", lg: "row" },
                                    marginBottom: "12px",
                                    alignItems: {
                                      xs: "flex-start",
                                      lg: "center",
                                    },
                                  }}
                                >
                                  <Stack
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                      alignItems: "center",
                                      justifyContent: {
                                        xs: "flex-start",
                                        lg: "unset",
                                      },
                                    }}
                                  >
                                    <IconButton
                                      onClick={() => handlePreviewClick(law)}
                                      sx={{ p: 0, ml: 0.5, mr: 0.5 }}
                                    >
                                      <Tooltip title="View">
                                        <Eye
                                          size={16}
                                          color="#2196f3"
                                          style={{ marginRight: "8px" }}
                                        />
                                      </Tooltip>
                                    </IconButton>
                                    <IconButton
                                      sx={{
                                        p: 0,
                                        "&:hover": {
                                          backgroundColor: "transparent",
                                        },
                                      }}
                                    >
                                      {bookmarkedCaseIds.has(law.id) ? (
                                        <>
                                          <Tooltip title="Remove bookmark">
                                            <Award
                                              onClick={() =>
                                                handleUnBookmark(law.id)
                                              }
                                              size={16}
                                              color="#d2042d"
                                            />
                                          </Tooltip>
                                        </>
                                      ) : (
                                        <>
                                          <Tooltip title="Bookmark">
                                            <Bookmark
                                              onClick={() =>
                                                handleBookmarkClick(law)
                                              }
                                              sx={{
                                                color: "#d2042d",
                                                "&:hover": {
                                                  backgroundColor:
                                                    "transparent",
                                                },
                                              }}
                                              size={16}
                                            />
                                          </Tooltip>
                                        </>
                                      )}
                                    </IconButton>
                                    <Typography
                                      sx={{
                                        fontSize: { xs: "13px", lg: "14px" },
                                      }}
                                    >
                                      {formatCaseTitle(law?.title)}
                                    </Typography>
                                  </Stack>
                                  <Stack
                                    sx={{
                                      display: "flex",
                                      flexDirection: {
                                        xs: "column",
                                        lg: "row",
                                      },
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: { xs: "13px", lg: "14px" },
                                      }}
                                    >
                                      ({law.label}) -
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: { xs: "13px", lg: "14px" },
                                      }}
                                    >
                                      {formatCourtName(law.court)}
                                    </Typography>
                                  </Stack>
                                </Box>
                                <CaseLawPreviewDialog
                                  open={openPreview}
                                  onClose={() => {
                                    setOpenPreview(false);
                                    dispatch({ type: "CASE_DETAIL_RESET" });
                                  }}
                                />
                              </React.Fragment>
                            ))}
                          {message.text.references?.filter(
                            (reference) => reference.type === "case",
                          ).length > 0 && (
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                marginBottom: "6px",
                                marginTop: 2,
                                fontSize: { xs: "13px", lg: "14px" },
                              }}
                            >
                              Relevant Case Laws:
                            </Typography>
                          )}
                          {message.text.references
                            ?.filter((reference) => reference.type === "case")
                            ?.map((law, index) => (
                              <React.Fragment key={index}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    marginBottom: "12px",
                                    // backgroundColor: "#f9f9f9",
                                    // alignItems: "center",
                                    width: "100%",
                                    gap: 1,
                                  }}
                                >
                                  {/* Icons and Title Section */}
                                  <Stack
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                      alignItems: "flex-start",
                                      // minWidth: 0, // Important for text truncation
                                      // maxWidth: { xs: "100%", lg: "75%" },
                                    }}
                                  >
                                    <IconButton
                                      onClick={() => handlePreviewClick(law)}
                                      sx={{
                                        p: 0,
                                        ml: 0.8,
                                        mr: "4px",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Tooltip title="View">
                                        <Eye
                                          size={16}
                                          color="#2196f3"
                                          style={{ marginRight: "8px" }}
                                        />
                                      </Tooltip>
                                    </IconButton>

                                    <IconButton
                                      sx={{
                                        p: 0,
                                        mr: "8px",
                                        flexShrink: 0,
                                        "&:hover": {
                                          backgroundColor: "transparent",
                                        },
                                      }}
                                    >
                                      {bookmarkedCaseIds.has(law.id) ? (
                                        <Tooltip title="Remove bookmark">
                                          <Award
                                            onClick={() =>
                                              handleUnBookmark(law.id)
                                            }
                                            size={16}
                                            color="#d2042d"
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title="Bookmark">
                                          <Bookmark
                                            onClick={() =>
                                              handleBookmarkClick(law)
                                            }
                                            sx={{
                                              color: "#d2042d",
                                              "&:hover": {
                                                backgroundColor: "transparent",
                                              },
                                            }}
                                            size={16}
                                          />
                                        </Tooltip>
                                      )}
                                    </IconButton>

                                    {/* Title with ellipsis and tooltip */}
                                    <Tooltip
                                      title={formatCaseTitle(law.title)}
                                      placement="top"
                                      arrow
                                      PopperProps={{
                                        modifiers: [
                                          {
                                            name: "offset",
                                            options: {
                                              offset: [0, -4],
                                            },
                                          },
                                        ],
                                      }}
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: "white",
                                            color: "text.primary",
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                            fontSize: "13px",
                                            maxWidth: "400px",
                                            boxShadow:
                                              "0 4px 20px rgba(0,0,0,0.1)",
                                            "& .MuiTooltip-arrow": {
                                              color: "white",
                                              "&::before": {
                                                border: "1px solid #e0e0e0",
                                              },
                                            },
                                          },
                                        },
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: { xs: "13px", lg: "14px" },
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "wrap",
                                          maxLines: 2,
                                          minWidth: 0, // Important for text truncation in flex
                                          cursor: "default",
                                        }}
                                      >
                                        {formatCaseTitle(law.title)}
                                      </Typography>

                                      {/* Court and Label Section */}
                                      <Typography
                                        fontWeight="bold"
                                        sx={{
                                          textOverflow: "ellipsis",
                                          whiteSpace: "wrap",
                                          fontSize: { xs: "13px", lg: "14px" },
                                        }}
                                      >
                                        {`(${law.label}) `}
                                        {!isMobileView && "-"}{" "}
                                        {formatCourtName(law.court)}
                                      </Typography>
                                    </Tooltip>
                                  </Stack>
                                </Box>

                                <CaseLawPreviewDialog
                                  open={openPreview}
                                  onClose={() => {
                                    setOpenPreview(false);
                                    dispatch({ type: "CASE_DETAIL_RESET" });
                                  }}
                                />
                              </React.Fragment>
                            ))}
                          {!message.isUser && (
                            <Stack
                              flexDirection="row"
                              sx={{ marginTop: "10px" }}
                            >
                              <Tooltip
                                title={isCopied ? "Copied" : "Copy"}
                                arrow
                                PopperProps={{
                                  sx: {
                                    "& .MuiTooltip-tooltip": {
                                      backgroundColor: "#101936",
                                    },
                                    "& .MuiTooltip-arrow": {
                                      color: "#101936",
                                    },
                                  },
                                }}
                              >
                                <IconButton
                                  sx={{
                                    color: isCopied ? "#4caf50" : "#2196f3",
                                  }}
                                  onClick={() =>
                                    handleCopyClick(message.text.response)
                                  }
                                >
                                  {isCopied ? (
                                    <CheckCircle
                                      size={16}
                                      color="#4caf50"
                                      style={{ marginRight: "8px" }}
                                    />
                                  ) : (
                                    <Copy
                                      size={16}
                                      color="#2196f3"
                                      style={{ marginRight: "8px" }}
                                    />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title="Bad Response"
                                arrow
                                PopperProps={{
                                  sx: {
                                    "& .MuiTooltip-tooltip": {
                                      backgroundColor: "#101936",
                                    },
                                    "& .MuiTooltip-arrow": {
                                      color: "#101936",
                                    },
                                  },
                                }}
                              >
                                <IconButton
                                  disabled={feedbackGiven[messageIndex]}
                                  sx={{ color: "#d32f2f" }}
                                  onClick={() =>
                                    handleFeedbackClick(
                                      message.text.response,
                                      message.text.references,
                                      messageIndex,
                                    )
                                  }
                                >
                                  <ThumbsDown
                                    size={16}
                                    color="#d32f2f"
                                    style={{ marginRight: "8px" }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
                {/* Thinking / tool-progress indicator — visible while loading but before text arrives */}
                {isLoading && !streamingText && (
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    flexDirection="row"
                    width="100%"
                  >
                    <Avatar
                      variant="circular"
                      sx={{
                        width: 32,
                        height: 32,
                        marginRight: 2,
                        padding: 0.5,
                        backgroundColor: "white",
                        "& img": {
                          objectFit: "contain",
                          width: "100%",
                          height: "100%",
                        },
                      }}
                      src={modelLogo}
                    />
                    {activeTools.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                          mt: 0.5,
                        }}
                      >
                        {activeTools.map((tool) => (
                          <Box
                            key={tool.tool_name}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {tool.status === "running" ? (
                              <CircularProgress
                                size={12}
                                sx={{ color: "#0073A6" }}
                              />
                            ) : (
                              <CheckCircle size={12} color="#22c55e" />
                            )}
                            <Typography
                              variant="caption"
                              sx={{ color: "#6B7280", fontSize: "13px" }}
                            >
                              {tool.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <LoadingAnimationWithText text="Thinking" />
                    )}
                  </Box>
                )}

                {/* Live streaming message bubble */}
                {streamingText && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      width: "100%",
                      mb: 3,
                    }}
                  >
                    <Avatar
                      variant="circle"
                      sx={{
                        display: { xs: "none", md: "flex" },
                        width: "36px",
                        height: "36px",
                        marginRight: "12px",
                        marginTop: "4px",
                        padding: "4px",
                        background: "white",
                        border: "1px solid #E2E8F0",
                        "& img": {
                          objectFit: "contain",
                          width: "100%",
                          height: "100%",
                        },
                      }}
                      src={modelLogo}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FFFFFF",
                        color: "#111827",
                        alignSelf: "flex-start",
                        borderRadius: "16px",
                        border: "1px solid #E2E8F0",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                        maxWidth: { xs: "90%", md: "80%" },
                        minWidth: "200px",
                      }}
                    >
                      {/* Tool chips */}
                      {activeTools.length > 0 && (
                        <Box
                          sx={{
                            px: 2.5,
                            pt: 1.5,
                            pb: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                            borderBottom: "1px solid #F1F5F9",
                          }}
                        >
                          {activeTools.map((tool) => (
                            <Box
                              key={tool.tool_name}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: "12px",
                                backgroundColor:
                                  tool.status === "done"
                                    ? "#F0FDF4"
                                    : "#F0F9FF",
                                border: `1px solid ${tool.status === "done" ? "#BBF7D0" : "#BAE6FD"}`,
                              }}
                            >
                              {tool.status === "running" ? (
                                <CircularProgress
                                  size={10}
                                  sx={{ color: "#0073A6" }}
                                />
                              ) : (
                                <CheckCircle size={10} color="#22c55e" />
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    tool.status === "done"
                                      ? "#16a34a"
                                      : "#0073A6",
                                  fontSize: "11px",
                                  fontWeight: 500,
                                }}
                              >
                                {tool.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Streaming text */}
                      <Typography
                        variant="body1"
                        component="div"
                        sx={{
                          width: "100%",
                          padding: { xs: "16px", md: "20px" },
                          color: "inherit",
                          boxSizing: "border-box",
                          fontSize: { xs: "14px", md: "15px" },
                          lineHeight: 1.6,
                          fontFamily: "'Inter', 'Inter Fallback', sans-serif",
                        }}
                      >
                        <Markdown
                          components={{
                            ul: ({ ...props }) => (
                              <ul
                                style={{
                                  ...markdownStyles.ul,
                                  fontSize: isMobileView ? "13px" : "14px",
                                }}
                                {...props}
                              />
                            ),
                            ol: ({ ...props }) => (
                              <ol
                                style={{
                                  ...markdownStyles.ol,
                                  fontSize: isMobileView ? "13px" : "14px",
                                }}
                                {...props}
                              />
                            ),
                            li: ({ ...props }) => (
                              <li
                                style={{
                                  ...markdownStyles.li,
                                  fontSize: isMobileView ? "13px" : "14px",
                                }}
                                {...props}
                              />
                            ),
                            p: ({ ...props }) => (
                              <Typography
                                sx={{
                                  ...markdownStyles.p,
                                  fontSize: { xs: "13px", lg: "14px" },
                                }}
                                {...props}
                                component="p"
                              />
                            ),
                            pre: ({ ...props }) => (
                              <Typography
                                style={markdownStyles.pre}
                                {...props}
                                component="pre"
                              />
                            ),
                            strong: ({ ...props }) => (
                              <Typography
                                sx={{
                                  fontSize: { xs: "14px", lg: "15px" },
                                  fontWeight: "bold",
                                }}
                                {...props}
                                component="strong"
                              />
                            ),
                          }}
                        >
                          {streamingText}
                        </Markdown>
                        {/* Blinking cursor */}
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            width: "2px",
                            height: "1em",
                            backgroundColor: "#0073A6",
                            ml: "2px",
                            verticalAlign: "text-bottom",
                            "@keyframes blink": {
                              "0%, 100%": { opacity: 1 },
                              "50%": { opacity: 0 },
                            },
                            animation: "blink 1s step-end infinite",
                          }}
                        />
                      </Typography>
                    </Box>
                  </Box>
                )}
                <div ref={chatContainerEndRef} />
              </Box>
            </Box>
          )}
          {showScrollButton && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                mb: 2,
              }}
            >
              <Fab
                size="medium"
                sx={{
                  color: "white",
                  backgroundColor: "#0073A6",
                  boxShadow: "0 4px 12px rgba(0, 115, 166, 0.3)",
                  "&:hover": {
                    boxShadow: "0 6px 16px rgba(0, 115, 166, 0.4)",
                    backgroundColor: "#005A8B",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
                onClick={scrollToBottom}
              >
                <ChevronDown size={24} />
              </Fab>
            </Box>
          )}
          <AlertDialog
            title="Message Limit Exceeded"
            description="You have reached the free chat limit. You can make 6 queries with free account."
            open={openAlertDialog}
            handleClose={handleCloseAlertDialog}
          />
          <Feedback
            open={openFeedbackDialog}
            modelResponse={response}
            feedbackQuery={feedbackQuery}
            references={feedbackRefs}
            handleClose={handleCloseFeedbackDialog}
            messageIndex={messageIndex}
            feedbackGiven={handleFeedbackGiven}
          />
          {/* Input field for chat view */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              padding: { xs: "16px", md: "24px" },
            }}
          >
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                maxWidth: { xs: "100%", md: "768px", lg: "900px" },
                width: "100%",
                margin: "0 auto",
              }}
            >
              <Box sx={{ position: "relative", width: "100%" }}>
                {/* Auto-growing TextField */}
                <Box
                  sx={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: "20px",
                    border: "1px solid #E2E8F0",
                    padding: "20px",
                    minHeight: "60px",
                    transition: "all 0.2s ease-in-out",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    "&:hover": {
                      borderColor: "#0073A6",
                      boxShadow: "0 4px 12px rgba(0, 115, 166, 0.12)",
                    },
                    "&:focus-within": {
                      borderColor: "#0073A6",
                      boxShadow:
                        "0 0 0 3px rgba(0, 115, 166, 0.08), 0 4px 12px rgba(0, 115, 166, 0.12)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      maxHeight: "120px",
                      minHeight: "24px",
                      mb: "12px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "#fff",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "#D1D5DB",
                        borderRadius: "6px",
                      },
                    }}
                  >
                    <TextField
                      inputRef={inputRef}
                      fullWidth
                      multiline
                      variant="standard"
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        // Handle Shift+Enter for new line
                        if (e.key === "Enter" && e.shiftKey) {
                          // Allow default behavior (new line)
                          return;
                        }
                        // Handle Enter for send (call your existing handler)
                        handleKeyDown(e);
                      }}
                      value={isListening ? "" : inputMessage}
                      placeholder={
                        isListening ? "" : "Ask any legal question..."
                      }
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          padding: "0px 8px",
                          fontSize: { xs: "14px", sm: "15px" },
                          lineHeight: 1.4,
                          minHeight: "20px",
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          resize: "none",
                          "&::placeholder": {
                            color: "#9e9e9e",
                            opacity: 1,
                            fontWeight: 400,
                          },
                          "&:focus": {
                            outline: "none",
                          },
                          scrollBehavior: "smooth",
                        },
                        "& .MuiInputBase-root": {
                          alignItems: "flex-start",
                        },
                      }}
                    />
                  </Box>

                  {/* Button Container - Bottom Right with proper spacing */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* Chat Mode Selector - Compact Version */}
                      {/* <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {[
                          {
                            label: "Hybrid",
                            value: "balanced",
                            icon: <GitBranch size={18} />,
                          },
                          {
                            label: "Statute",
                            value: "precise",
                            icon: <BookOpen size={18} />,
                          },
                          {
                            label: "Case",
                            value: "case law",
                            icon: <FileText size={18} />,
                          },
                        ].map((mode, index) => (
                          <Tooltip
                            key={`${uuidv4()}-${index}`}
                            title={mode.label}
                            placement="top"
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleChangeChat({
                                  target: { value: mode.value },
                                })
                              }
                              sx={{
                                padding: "8px",
                                color:
                                  mode.value === alignment
                                    ? "#0073A6"
                                    : "#9CA3AF",
                                backgroundColor:
                                  mode.value === alignment
                                    ? "#F0F9FF"
                                    : "transparent",
                                borderRadius: "8px",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor:
                                    mode.value === alignment
                                      ? "#E0F2FE"
                                      : "#F3F4F6",
                                },
                              }}
                            >
                              {mode.icon}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box> */}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        alignItems: "center",
                      }}
                    >
                      {/* Lightning/Rephrase Button */}
                      <Tooltip
                        title={
                          inputMessage.trim()
                            ? "Rephrase with AI"
                            : "Enter message to rephrase"
                        }
                        placement="bottom"
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            sx={{
                              padding: "10px",
                              color: "#0073A6",
                              borderRadius: "8px",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: "#F0F9FF",
                                transform: "scale(1.05)",
                              },
                              "&:active": {
                                transform: "scale(0.95)",
                              },
                            }}
                            onClick={handleRewrite}
                            disabled={!inputMessage.trim() || isRewriting}
                          >
                            {isRewriting ? (
                              <CircularProgress
                                size={18}
                                sx={{ color: "#0073A6" }}
                              />
                            ) : (
                              <Zap size={18} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Info Button - Moved to left of audio controls */}
                      <Tooltip title="Information" placement="top" arrow>
                        <IconButton
                          size="small"
                          onClick={handleClickInfoButton}
                          sx={{
                            padding: "10px",
                            color: "#6B7280",
                            borderRadius: "50%",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#F3F4F6",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <Info size={18} />
                        </IconButton>
                      </Tooltip>
                      {isListening ? (
                        <>
                          <Tooltip title="Discard" placement="top" arrow>
                            <IconButton
                              size="small"
                              onClick={cancelVoiceInput}
                              sx={{
                                padding: "8px",
                                transition: "all 0.2s ease",
                                borderRadius: "50%",
                                "&:hover": {
                                  backgroundColor: "rgba(158, 158, 158, 0.1)",
                                  transform: "scale(1.05)",
                                },
                              }}
                            >
                              <Trash2
                                sx={{
                                  color: "#9e9e9e",
                                  fontSize: "16px",
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Stop" placement="top" arrow>
                            <IconButton
                              size="small"
                              sx={{
                                padding: "8px",
                                backgroundColor: "#d32f2f",
                                color: "#fff",
                                borderRadius: "50%",
                                animation: `${pulse} 1.1s infinite`,
                                transition: "all 0.3s ease-in-out",
                                transform: "scale(1.05)",
                                boxShadow: "0 0 12px rgba(211, 47, 47, 0.4)",
                                "&:hover": {
                                  backgroundColor: "#c62828",
                                  boxShadow: "0 0 16px rgba(198, 40, 40, 0.6)",
                                  transform: "scale(1.1)",
                                },
                              }}
                              onClick={acceptVoiceInput}
                            >
                              <Square sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Speak" placement="bottom" arrow>
                            <IconButton
                              size="small"
                              sx={{
                                padding: "10px",
                                color: "#0073A6",
                                borderRadius: "50%",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor: "#F0F9FF",
                                  transform: "scale(1.05)",
                                },
                                "&:active": {
                                  transform: "scale(0.95)",
                                },
                              }}
                              onClick={startRecognition}
                            >
                              <Mic size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              inputMessage.trim()
                                ? "Send message"
                                : "Enter message to send"
                            }
                            placement="bottom"
                            arrow
                          >
                            <span>
                              <IconButton
                                id="legal-chat-send-button"
                                size="small"
                                sx={{
                                  padding: "10px",
                                  color: inputMessage.trim()
                                    ? "#ffffff"
                                    : "#9CA3AF",
                                  backgroundColor: inputMessage.trim()
                                    ? "#0073A6"
                                    : "#F3F4F6",
                                  borderRadius: "50%",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: inputMessage.trim()
                                      ? "#005A8B"
                                      : "#E5E7EB",
                                    transform: inputMessage.trim()
                                      ? "scale(1.05)"
                                      : "none",
                                  },
                                  "&:active": {
                                    transform: inputMessage.trim()
                                      ? "scale(0.95)"
                                      : "none",
                                  },
                                  "&:disabled": {
                                    color: "#D1D5DB",
                                    backgroundColor: "#F9FAFB",
                                  },
                                }}
                                onClick={handleSendMessage}
                                disabled={!inputMessage.trim() || isListening}
                              >
                                <Send size={18} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
                {isListening && (
                  <AudioVisualizer
                    isListening={isListening}
                    audioLevel={audioData}
                    component="legalChat"
                  />
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
        <ToastContainer />
      </Box>
      {snackbarMessage && (
        <Snackbar
          open={!!snackbarMessage}
          autoHideDuration={6000}
          onClose={() => setSnackbarMessage(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarMessage(null)}
            severity={snackbarMessage.type}
            sx={{ width: "100%" }}
          >
            {snackbarMessage.text}
          </Alert>
        </Snackbar>
      )}
      <StatuteContentDialog
        open={openModal}
        onClose={handleCloseModal}
        label={modalContent?.label}
        content={modalContent?.content}
        isMobile={isMobile}
      />

      {openBookmarkDialog && (
        <BookmarkDialog
          open={openBookmarkDialog}
          onClose={handleCloseBookmarkDialog}
          selectedCase={selectedCase}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setBookmarkedCaseIds={setBookmarkedCaseIds}
        />
      )}

      {openStatuteModal && (
        <BookmarkDialog
          open={openStatuteModal}
          onClose={handleCloseStatuteBookmarkDialog}
          selectedStatute={selectedStatute}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setBookmarkedStatuteIds={setBookmarkedStatuteIds}
        />
      )}
      <InfoDialog
        open={openInfoDialog}
        handleClose={handleCloseInfoDialog}
        isLegalChat={true}
      />
    </>
  );
};

export default memo(ChatInterface);

ChatInterface.propTypes = {
  toggleSidebar: PropTypes.func.isRequired,
};
